import { useProductsBySlug } from '@gik/api/products/productsBySlug';
import { FetchProvider } from '@gik/core/components/DynamicComponent/FetchProvider';
import { SkeletonNetworkError, SkeletonNetworkRetryMessage } from '@gik/core/components/NetworkError/NetworkError';
import { withMeta } from '@gik/core/hocs/withMeta';
import { withTitle } from '@gik/core/hocs/withTitle';
import type { Product } from '@gik/core/models/gik/Product';
import * as NetworkStoreApi from '@gik/core/stores/NetworkStore.ts';
import NetworkStore from '@gik/core/stores/NetworkStore.ts';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { ButtonBone, CircleBone, FrameBone, ParagraphBone } from '@gik/ui-legacy/SkeletonLoader';
import { useStore } from '@nanostores/react';
import React from 'react';
import type { ProductSectionProps } from './ProductSection';
import { ProductSection } from './ProductSection';
import { Layout } from '@gik/ui';

// Custom skeleton for product section
type ProductSectionSkeletonProps = {
  showRetrying: boolean;
  showError: boolean;
  onReload: () => void;
  inkindRouteId?: string;
};

const ProductSectionSkeleton = ({ showRetrying, showError, onReload, inkindRouteId }: ProductSectionSkeletonProps) => {
  const bem = useBemCN('product-section-skeleton');

  return (
    <div className="relative">
      <div className="gik-product-section__main" role="layout">
        {inkindRouteId && (
          <div className="mb-4">
            <FrameBone style={{ '--width': '400px', '--height': '252px' }} />
          </div>
        )}

        <header className="flex flex-col md:flex-row gap-8 mb-8 mt-4" role="header">
          <div className="flex-1">
            {/* Product image skeleton */}
            <FrameBone style={{ '--width': '400px', '--height': '252px' }} />
          </div>
          <div className="flex-1 space-y-4">
            {/* Product title */}
            <ParagraphBone words={4} />
            <ParagraphBone words={4} />
            {/* Product price */}
            <ParagraphBone words={1} />
            <br />

            <FrameBone style={{ '--width': '100%', '--height': '38px' }} />

            <ParagraphBone words={2} />

            <Layout horizontal>
              <FrameBone style={{ '--width': '20px', '--height': '20px' }} />
              <ParagraphBone words={4} />
            </Layout>

            <Layout horizontal>
              <FrameBone style={{ '--width': '88px', '--height': '38px' }} />
              <FrameBone style={{ '--width': '112px', '--height': '38px' }} />
            </Layout>
          </div>
        </header>

        <main className="space-y-4 gik-product-section__main" role="content">
          {/* Product description */}
          <ParagraphBone words={8} />
          <ParagraphBone words={12} />
          <ParagraphBone words={6} />
          <ParagraphBone words={8} />
          <ParagraphBone words={12} />
          <ParagraphBone words={6} />
          <ParagraphBone words={8} />
          <ParagraphBone words={12} />
          <ParagraphBone words={6} />
        </main>
      </div>
      {showRetrying && !showError && <SkeletonNetworkRetryMessage />}
      {showError && <SkeletonNetworkError onReload={onReload} />}
    </div>
  );
};

function ProductSectionWithDataComp(props: ProductSectionProps) {
  const { productSlug, inkindRouteId } = props;
  const requestKey = `product-section-${productSlug}`;

  // Subscribe to NetworkStore so the component re-renders when the request
  // transitions through loading → retrying → failed states (mirrors WishlistRows pattern).
  useStore(NetworkStore);

  const { data: products, mutate } = useProductsBySlug({ slug: [productSlug], perPage: 1 }, { requestKey });

  const product = products?.[0];

  // ponytail: SEO meta must render at SSR before the isSuccess gate (false until client fetch),
  // otherwise crawlers see the skeleton with no title/description. next/head dedupes by key,
  // so ProductSection re-rendering the same meta once loaded is harmless.
  const seoMeta = product
    ? withMeta(
        {
          title: product.name,
          imageUrl: product.images?.[0],
          description: product.description,
          keywords: product.situationKeyword,
          type: 'product',
        },
        null
      )
    : null;

  // Show skeleton with retry/error handling when no data or NetworkStore indicates issues
  if (!products || !NetworkStoreApi.isSuccess(requestKey)) {
    return (
      <>
        {seoMeta}
        <ProductSectionSkeleton
          showRetrying={NetworkStoreApi.shouldShowRetryMessage(requestKey)}
          showError={NetworkStoreApi.isError(requestKey)}
          inkindRouteId={inkindRouteId}
          onReload={() => {
            NetworkStoreApi.reload(requestKey);
            mutate();
          }}
        />
      </>
    );
  }

  return withTitle(
    {
      title: products?.[0]?.name,
    },
    <ProductSection {...props} products={products} inkindRouteId={inkindRouteId} />
  );
}

export const ProductSectionWithData = withComponentErrorBoundary(ProductSectionWithDataComp);
